import React, {useEffect, useMemo, useState} from 'react';
import {apiAction} from '@arboxappv4/shared/src/helpers/HTTP';
import {useDispatch, useSelector} from 'react-redux';
import {updateFlags, updateToastMessage} from '@arboxappv4/shared/src/redux/stepperSlice';
import {Spin} from 'antd';
import {t} from 'i18next';
import orderBy from 'lodash-es/orderBy';
import styled from 'styled-components';
import {Btn, FlexColumnSection, FlexRowSection} from '../../../styles/globalStyles';
import {Colors} from '@arboxappv4/shared/src/styles/Colors';
import useScreenSize from '../../../helpers/hooks/useScreenSize';
import {isSeriesEventBooked, isSeriesEventFull} from '../../../helpers/functions';
import TrackActivitySelectionHeader from './TrackActivitySelectionHeader';
import TrackCourseSection from './TrackCourseSection';

const TrackActivitySelection = ({handleSubmit, values, setFieldValue}) => {
    const flags = useSelector(state => state.stepper.flags);
    const user = useSelector(state => state.user.data);
    const [fetching, setFetching] = useState(false);
    const [trackCourses, setTrackCourses] = useState(null);
    const [filteredCourses, setFilteredCourses] = useState(null);
    const [selectedCourses, setSelectedCourses] = useState([]);
    const dispatch = useDispatch();
    const {isMobile} = useScreenSize();

    const trackSessionPerWeek = flags.trackInfo?.session_per_week ?? 1;

    useEffect(() => {
        if (flags.trackInfo) {
            fetchTrackCourses();
        }
    }, [flags.trackInfo?.id, user?.id]);

    useEffect(() => {
        if (trackCourses?.length && values.courses?.length) {
            setSelectedCourses(values.courses);
        }
    }, [trackCourses]);

    useEffect(() => {
        if (values.activitySelectionReady && flags.activitySelectionReady) {
            dispatch(updateFlags({activitySelectionReady: false}));
            handleSubmit();
        }
    }, [values.activitySelectionReady, flags.activitySelectionReady]);

    const fetchTrackCourses = async () => {
        setFetching(true);
        const res = await apiAction('getTrackCourses', 'post', {
            series_track_id: flags.trackInfo.id,
            user_id: user?.id ?? null,
        });
        setTrackCourses(res);
        setFetching(false);
    };

    const getTrackCoursesSelection = ({selectedCourses, trackCourses, trackSessionPerWeek}) => {
        const isEmpty = !trackCourses?.length;
        let maxSelectableCount = 0;
    
        (trackCourses ?? []).forEach(course => {
            const series = course.series;
            let availableInCourse = 0;
            (series?.series_events ?? []).forEach(event => {
                if (!isSeriesEventFull(series, event.id) && !isSeriesEventBooked(series, event.id)) {
                    availableInCourse++;
                }
            });
            if (course.session_per_week != null) {
                maxSelectableCount += Math.min(availableInCourse, course.session_per_week);
            } else {
                maxSelectableCount += availableInCourse;
            }
        });
    
        const requiredCount = Math.min(trackSessionPerWeek ?? 0, maxSelectableCount);
        const selectedCount = (selectedCourses ?? []).reduce((sum, course) => sum + (course.series_event_ids?.length ?? 0), 0);
        const canProceed = !isEmpty && requiredCount > 0 && selectedCount >= requiredCount;
    
        return {
            requiredCount,
            selectedCount,
            canProceed,
        };
    };

    const trackCoursesSelection = useMemo(
        () => getTrackCoursesSelection({
            selectedCourses,
            trackCourses,
            trackSessionPerWeek,
        }),
        [selectedCourses, trackCourses, trackSessionPerWeek],
    );

    const sortedCourses = useMemo(
        () => orderBy(filteredCourses ?? [], [course => course.series?.series_name ?? '']),
        [filteredCourses],
    );

    const onFiltered = (filtered) => {
        setFilteredCourses(filtered);
    };

    const onSelectSeriesEvent = (seriesId, seriesEventId) => {
        const seriesExistingSelection = selectedCourses.find(course => course.series_id === seriesId);
        const isAlreadySelected = (seriesExistingSelection?.series_event_ids ?? []).includes(seriesEventId);

        if (isAlreadySelected) {
            const updatedEventIds = (seriesExistingSelection.series_event_ids ?? []).filter(id => id !== seriesEventId);
            const updatedCourses = selectedCourses
                .map(course =>
                    course.series_id === seriesId
                        ? {...course, series_event_ids: updatedEventIds}
                        : course
                )
                .filter(course => (course.series_event_ids?.length ?? 0) > 0);
            setSelectedCourses(updatedCourses);
            return;
        }

        if (!seriesExistingSelection) {
            setSelectedCourses([...selectedCourses, {series_id: seriesId, series_event_ids: [seriesEventId]}]);
            return;
        }

        const updatedSeriesEventIds = [...(seriesExistingSelection.series_event_ids ?? []), seriesEventId];
        const updatedCourses = selectedCourses.map(course =>
            course.series_id === seriesId
                ? {...course, series_event_ids: updatedSeriesEventIds}
                : course
        );
        setSelectedCourses(updatedCourses);
    };

    const onContinue = () => {
        const {requiredCount, selectedCount, canProceed} = trackCoursesSelection;

        if (selectedCount < requiredCount) {
            dispatch(updateToastMessage({
                content: t('track-register-frequency-required', {count: requiredCount}),
                title: t('error'),
                type: 'error',
            }));
            return;
        }

        const allSeriesEvents = (filteredCourses ?? []).flatMap(course => course.series?.series_events ?? []);
        const selectedSeriesEvents = selectedCourses.flatMap(course =>
            (course.series_event_ids ?? []).map(eventId => allSeriesEvents.find(event => event.id === eventId)).filter(Boolean)
        );
        const seen = [];
        // const hasOverlap = selectedSeriesEvents.some(seriesEvent => {
        //     const key = `${seriesEvent.day_of_week}|${seriesEvent.start}|${seriesEvent.end}`;
        //     if (seen.includes(key)) {
        //         return true;
        //     }
        //     seen.push(key);
        //     return false;
        // });
        // if (hasOverlap) {
        //     dispatch(updateToastMessage({
        //         content: t('track-overlapping-times-toast'),
        //         title: t('error'),
        //         type: 'error',
        //     }));
        //     return;
        // }
        if(requiredCount === 0) {
            dispatch(updateToastMessage({
                content: t('no-available-activities'),
                title: t('error'),
                type: 'error',
            }));
        }

        if (!canProceed) {
            return;
        }

        const payload = (selectedCourses ?? []).filter(course => (course.series_event_ids?.length ?? 0) > 0).map(course => ({
            series_id: course.series_id,
            series_event_ids: course.series_event_ids,
        }));

        setFieldValue('courses', payload);
        setFieldValue('activitySelectionReady', true);
        dispatch(updateFlags({activitySelectionReady: true}));
    };

    return (
        <PageWrapper isMobile={isMobile}>
            <Spin spinning={fetching}/>
            {!fetching && trackCourses?.length === 0 &&
                <span>{t('no-activities-in-track')}</span>
            }
            {!fetching && trackCourses?.length > 0 &&
                <ContentColumn>
                    <TrackActivitySelectionHeader
                        trackCourses={trackCourses}
                        onFiltered={onFiltered}
                        selectedCount={trackCoursesSelection.selectedCount}
                        requiredCount={trackCoursesSelection.requiredCount}
                    />
                    <CoursesList>
                        {sortedCourses.length === 0 && <span>{t('track-activities-filter-empty')}</span>}
                        {sortedCourses.map(course => (
                            <TrackCourseSection
                                key={course.series_id ?? course.series?.id}
                                course={course}
                                selectedCourses={selectedCourses}
                                trackSessionPerWeek={trackSessionPerWeek}
                                onSelect={onSelectSeriesEvent}
                            />
                        ))}
                    </CoursesList>
                    <FooterRow isMobile={isMobile}>
                        <ContinueBtn type={'primary'} onClick={onContinue} width={isMobile ? '100%' : null}>
                            {t('continue')}
                        </ContinueBtn>
                    </FooterRow>
                </ContentColumn>
            }
        </PageWrapper>
    );
};

export default TrackActivitySelection;

const PageWrapper = styled(FlexColumnSection)`
    gap: 15px;
    padding: ${({isMobile}) => isMobile ? '0 20px' : '0'};
    height: 100%;
    min-height: 0;
    overflow: hidden;
    box-sizing: border-box;
`;

const ContentColumn = styled(FlexColumnSection)`
    gap: 16px;
    height: 100%;
    min-height: 0;
    overflow: hidden;
`;

const CoursesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    padding-top: 4px;
`;

const FooterRow = styled(FlexRowSection)`
    height: 38px;
    flex: none;
    justify-content: flex-end;
    padding-top: 8px;
`;

const ContinueBtn = styled(Btn)`
    && {
        background: ${Colors.siteBlack};
        border-color: ${Colors.siteBlack};
        height: 38px;
        min-width: 101px;
        font-size: 16px;
        font-weight: 500;
        border-radius: 5px;

        &:hover,
        &:focus {
            background: ${Colors.siteBlackHover};
            border-color: ${Colors.siteBlackHover};
        }
    }
`;
